import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircle, Clock } from "lucide-react";
import Navbar from "../components/Navbar";
import TabBar from "../components/TabBar";
import SupportChip from "../components/SupportChip";
import Title from "../components/Title";
import { useAuth } from "../contexts/AuthContext";
import { getTrainerEarnings } from "../services/api";

/** Types */
type TabKey = "paid" | "pending" | "upcoming";
type BaseSession = {
  id: string;
  date: string;
  title: string;
  amount: number;
  status?: string;
};
type UpcomingSession = BaseSession & { countdown: string };

/* ----------------------------- Row UI ----------------------------- */
function EarningRow({
  date,
  title,
  amount,
  rightAccent,
  stripe,
  footer,
}: {
  date: string;
  title: string;
  amount: number;
  rightAccent: React.ReactNode;
  stripe: "green" | "orange" | "red";
  footer?: React.ReactNode;
}) {
  const stripeClass =
    stripe === "green" ? "bg-[#2E8B57]" :
      stripe === "orange" ? "bg-[#F29D38]" :
        "bg-[#B94E3A]";

  return (
    <div className="bg-white border border-[#E1E1E1]">
      <div className="flex">
        <div className={`w-1 ${stripeClass}`} />
        <div className="flex-1 p-4">
          <div className="flex items-start justify-between">
            <div className="min-w-0 pr-3">
              <div className="text-[12px] text-[#7D7D7D] mb-1">{date}</div>
              <div className="text-[14px] font-extrabold text-[#2D2D2D]">{title}</div>
              {footer && <div className="mt-2">{footer}</div>}
            </div>
            <div className="text-right">{rightAccent}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------- Screen ----------------------------- */
const TrainerEarnings: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState<TabKey>("paid");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [totalEarnings, setTotalEarnings] = useState(0);
  const [paidSessions, setPaidSessions] = useState<BaseSession[]>([]);
  const [pendingSessions, setPendingSessions] = useState<BaseSession[]>([]);
  const [upcomingSessions, setUpcomingSessions] = useState<UpcomingSession[]>([]);

  useEffect(() => {
    const fetchEarnings = async () => {
      const trainerId = user?.id || user?._id;
      if (!trainerId) return;
      try {
        setLoading(true);
        const data = await getTrainerEarnings(trainerId);
        setTotalEarnings(data.totalEarnings ?? 0);
        setPaidSessions(data.paid ?? []);
        setPendingSessions(data.pending ?? []);
        setUpcomingSessions(data.upcoming ?? []);
      } catch (err) {
        console.error("Error fetching earnings:", err);
        setError("Failed to load earnings. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchEarnings();
  }, [user]);

  const sessions = useMemo(() => {
    if (activeTab === "paid") return paidSessions;
    if (activeTab === "pending") return pendingSessions;
    return upcomingSessions;
  }, [activeTab, paidSessions, pendingSessions, upcomingSessions]);

  return (
    <div className="h-[100dvh] w-full bg-[#F7F7F7] overflow-y-auto" style={{ WebkitOverflowScrolling: "touch" }}>
      <div className="mx-auto w-full min-h-[100dvh]">
        {/* Header / hero */}
        <header className="relative w-full bg-[linear-gradient(157.07deg,#3a3a3a_0%,#252525_81.65%)] text-white">
          <Navbar
            onBack={() => navigate("/trainer/profile")}
            background="transparent"
            spacerHeight={12}
            right={<SupportChip />}
          />

          <div className="px-5 pb-6 pt-4">
            <div className="text-center">
              <div className="text-[12px] tracking-wide text-[#717171] font-extrabold">
                YOUR TOTAL EARNINGS
              </div>

              <div className="mt-4 flex items-end justify-center gap-1">
                <span className="text-[#F29D38] text-[36px] leading-none font-extrabold italic">$</span>
                <span className="text-white italic font-extrabold text-[48px] leading-none">
                  {loading ? "—" : totalEarnings.toFixed(2)}
                </span>
              </div>

              <div className="mt-5 text-[12px] text-[#717171] text-center">
                <div>Your earnings will be sent to your bank account</div>
                <div>within 7 days.</div>
              </div>
            </div>
          </div>
        </header>

        {/* EARNINGS Title */}
        <section className="px-5 mt-6 mb-4">
          <Title text="// EARNINGS" />
        </section>

        {/* TabBar */}
        <TabBar
          tabs={["PAID", "PENDING", "UPCOMING"]}
          active={activeTab === "paid" ? 0 : activeTab === "pending" ? 1 : 2}
          onChange={(index) => {
            const tabs: TabKey[] = ["paid", "pending", "upcoming"];
            setActiveTab(tabs[index]);
          }}
        />

        {/* List */}
        <main className="w-full">
          <div className="px-5 py-4 pb-20">
            {loading ? (
              <div className="text-center py-12 text-[#717171] text-sm">Loading earnings…</div>
            ) : error ? (
              <div className="text-center py-12 text-red-500 text-sm">{error}</div>
            ) : (
              <div className="space-y-3">
                {activeTab === "paid" && (
                  sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-[#717171] text-sm">No paid sessions yet</div>
                    </div>
                  ) : (
                    (sessions as BaseSession[]).map((s) => (
                      <EarningRow
                        key={s.id}
                        date={s.date}
                        title={s.title}
                        amount={s.amount}
                        stripe="green"
                        rightAccent={
                          <span className="text-[#2E8B57] font-extrabold text-[18px]">
                            +${s.amount.toFixed(2)}
                          </span>
                        }
                      />
                    ))
                  )
                )}

                {activeTab === "pending" && (
                  sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-[#717171] text-sm">No pending payments</div>
                    </div>
                  ) : (
                    (sessions as BaseSession[]).map((s) => (
                      <EarningRow
                        key={s.id}
                        date={s.date}
                        title={s.title}
                        amount={s.amount}
                        stripe="orange"
                        rightAccent={
                          <span className="text-[#F29D38] font-extrabold text-[18px]">
                            +${s.amount.toFixed(2)}
                          </span>
                        }
                        footer={
                          <div className="flex items-center text-[#7D7D7D] text-[12px]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{s.status}</span>
                          </div>
                        }
                      />
                    ))
                  )
                )}

                {activeTab === "upcoming" && (
                  sessions.length === 0 ? (
                    <div className="text-center py-8">
                      <div className="text-[#717171] text-sm">No upcoming sessions</div>
                    </div>
                  ) : (
                    (sessions as UpcomingSession[]).map((s) => (
                      <EarningRow
                        key={s.id}
                        date={s.date}
                        title={s.title}
                        amount={s.amount}
                        stripe="red"
                        rightAccent={
                          <span className="text-[#2D2D2D] font-extrabold text-[18px]">
                            ${s.amount.toFixed(2)}
                          </span>
                        }
                        footer={
                          <div className="flex items-center text-[#7D7D7D] text-[12px]">
                            <Clock className="w-4 h-4 mr-1" />
                            <span>{s.countdown}</span>
                          </div>
                        }
                      />
                    ))
                  )
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
};

export default TrainerEarnings;
